import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BookmarkAdd as BookmarkAddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { bookingService } from '../services/bookingService';
import { BookingFilters } from '../services/filterPersistence';
import { FilterPreset } from '../types/filterPreset';

interface FilterPresetDropdownProps {
  currentFilters: BookingFilters;
  onApply: (filters: BookingFilters) => void;
  onClear?: (clearFn: () => void) => void;
}

const FilterPresetDropdown: React.FC<FilterPresetDropdownProps> = ({ currentFilters, onApply, onClear }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedPresetName, setSelectedPresetName] = useState<string | null>(null);

  useEffect(() => {
    if (onClear) onClear(() => setSelectedPresetName(null));
  }, [onClear]);

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bookingService.getFilterPresets();
      setPresets(data);
    } catch (err) {
      console.error('Error loading filter presets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = (preset: FilterPreset) => {
    onApply(preset.filters);
    setSelectedPresetName(preset.name);
    handleClose();
  };

  const handleSaveOpen = () => {
    setPresetName('');
    setSaveDialogOpen(true);
    handleClose();
  };

  const handleSaveClose = () => {
    setSaveDialogOpen(false);
    setPresetName('');
  };

  const handleSave = async () => {
    const trimmed = presetName.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const created = await bookingService.createFilterPreset(trimmed, currentFilters);
      setPresets((prev) => [...prev, created]);
      handleSaveClose();
    } catch (err) {
      console.error('Error saving filter preset:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event: React.MouseEvent, id: number) => {
    event.stopPropagation();
    setDeletingId(id);
    try {
      await bookingService.deleteFilterPreset(id);
      setPresets((prev) => prev.filter((p) => p.id !== id));
      setSelectedPresetName((prev) => {
        const deleted = presets.find((p) => p.id === id);
        return deleted && deleted.name === prev ? null : prev;
      });
    } catch (err) {
      console.error('Error deleting filter preset:', err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ExpandMoreIcon />}
        onClick={handleOpen}
        sx={{
          height: '40px',
          whiteSpace: 'nowrap',
          maxWidth: 200,
          ...(selectedPresetName ? { fontWeight: 600 } : {}),
        }}
      >
        {selectedPresetName ? (
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 140,
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          >
            {selectedPresetName}
          </Box>
        ) : (
          'Presets'
        )}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ style: { minWidth: 220, maxHeight: 360 } }}
      >
        <MenuItem onClick={handleSaveOpen}>
          <BookmarkAddIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <ListItemText primary="Create Filter Preset" />
        </MenuItem>

        <Divider />

        {loading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <ListItemText primary="Loading…" />
          </MenuItem>
        )}

        {!loading && presets.length === 0 && (
          <MenuItem disabled>
            <ListItemText
              primary="No saved presets"
              primaryTypographyProps={{ variant: 'body2', color: 'text.disabled' }}
            />
          </MenuItem>
        )}

        {!loading &&
          presets.map((preset) => (
            <MenuItem
              key={preset.id}
              sx={{
                pr: 1,
                ...(preset.name === selectedPresetName
                  ? { bgcolor: 'action.selected', fontWeight: 600 }
                  : {}),
              }}
            >
              <ListItemText
                primary={preset.name}
                onClick={() => handleApply(preset)}
                sx={{ cursor: 'pointer', flexGrow: 1 }}
              />
              <Tooltip title="Delete preset">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(e) => handleDelete(e, preset.id)}
                  disabled={deletingId === preset.id}
                  sx={{ ml: 1 }}
                >
                  {deletingId === preset.id ? (
                    <CircularProgress size={14} />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </MenuItem>
          ))}
      </Menu>

      <Dialog open={saveDialogOpen} onClose={handleSaveClose} maxWidth="xs" fullWidth>
        <DialogTitle>Create Filter Preset</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Preset Name"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
              inputProps={{ maxLength: 80 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Saves current search, filters, and sort settings.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!presetName.trim() || saving}
          >
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FilterPresetDropdown;
